import { noteCreatedAtToUnixSeconds } from '@/features/application/utils/notesTabHelpers'
import { resolveNoteAuthorName } from '@/lib/utils/noteFormatting'
import type { PublicNoteRow } from '../types'

/** Angular `processDefaultNotesandMessage` — notes that never show Edit. */
export const NON_EDITABLE_PUBLIC_DEFAULT_NOTES = [
  'First Month Rent Not Paid has been updated',
  'First Month Rent Not Paid has been updated (by reimbursement override)',
] as const

export function isPublicDefaultNoteRow(row: PublicNoteRow): boolean {
  return row.type === 'notes' || String(row.email_send) === '0'
}

/** Display name for a public notes row (Angular `messagerow?.name`). */
export function getPublicNoteAuthorName(row: PublicNoteRow): string {
  return resolveNoteAuthorName(row)
}

/**
 * Angular list middle column: `messagerow?.email_send_to`
 * (e.g. "Property", "Reimbursements", "Property and Reimbursements").
 */
export function getPublicNoteEmailSendTo(row: PublicNoteRow): string {
  const candidates = [
    row.email_send_to,
    row.emailSendTo,
    row.send_to,
    row.sendTo,
  ]
  for (const value of candidates) {
    const text = String(value ?? '').trim()
    if (text) return text
  }
  return ''
}

/** Message / note body for the right column (Angular `messagerow?.content`). */
export function getPublicNoteContent(row: PublicNoteRow): string {
  return String(row.content ?? row.message ?? row.notes ?? '').trim()
}

/**
 * Mirrors Angular `defaultnotesandmessage` `is_edit` flag for notes rows only.
 */
export function canEditPublicDefaultNote(
  row: PublicNoteRow,
  ctx: {
    isLibertyRole: boolean
    currentUserId: string | number | null | undefined
    now?: number
  },
): boolean {
  if (!isPublicDefaultNoteRow(row)) return false

  const isSystemHtml =
    row.is_system_html === '1' || row.is_system_html === true || row.is_system_html === 1
  if (isSystemHtml) return false

  const text = getPublicNoteContent(row)
  if ((NON_EDITABLE_PUBLIC_DEFAULT_NOTES as readonly string[]).includes(text)) return false

  if (!ctx.isLibertyRole) return false
  if (ctx.currentUserId == null || String(ctx.currentUserId) === '') return false
  if (String(row.fk_user_id ?? '') !== String(ctx.currentUserId)) return false

  const createdSec = noteCreatedAtToUnixSeconds(row.created_at)
  if (createdSec == null) return false

  const nowSec = Math.floor((ctx.now ?? Date.now()) / 1000)
  return nowSec - createdSec < 5 * 60
}

export function getPublicNoteRowId(row: PublicNoteRow): string | number | null {
  if (row.notes_id != null && String(row.notes_id) !== '') return row.notes_id as string | number
  if (row.note_id != null && String(row.note_id) !== '') return row.note_id as string | number
  if (row.id != null && String(row.id) !== '') return row.id as string | number
  return null
}
